import * as React from 'react'
import { flushSync } from 'react-dom'
import moment from 'moment'
import type { Moment } from 'moment'
import FlexView from '@/modules/shared/components/FlexView'
import {
  TimesheetModel,
  TimesheetCellModel,
  TimesheetRowModel,
} from '@/app/models/timesheetModel'
import { TimesheetDayActivityModel } from '@/app/models/timesheet/timesheetDayActivityModel'
import { TIME_FORMAT } from '@/app/constants/date'
import useTimesheetService from '@/modules/drivers/services/TimesheetService'

import EmptyState from '../components/timesheet/EmptyState'
import HeaderDayItem from './components/sheet/HeaderDayItem'
import RowInfoItem from './components/sheet/RowInfoItem'
import SummaryHeaderItem from './components/sheet/SummaryHeaderItem'
import CellItem from './components/sheet/CellItem'
import TimeSheetContextMenu from './components/contextmenu/TimeSheetContextMenu'
import ScheduleProvider, { useSchedule } from '../context/ScheduleContext'
import { useScheduleStore } from '@/modules/drivers/stores/scheduleStore'
import TimesheetDayTypePanel from './components/contextmenu/TimesheetDayTypePanel'
import TimesheetInfoPopup from './components/popup/TimesheetInfoPopup'
import { useTask } from '@/modules/shared/context/TaskContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { TaskType } from '@/app/constants/taskType'
import { TaskStatus } from '@/app/constants/taskStatus'
import { useTimesheet } from './context/useTimesheet'
import useDayTypesService from '@/modules/drivers/services/DayTypesService'
import { DayType } from '../schedule/constants/DayType'
import { DayType as CellDayType } from 'app/constants/dayType'

import WorkingDayChart from './components/sheet/WorkingDayChart'
import { GROUP_END_TITLE_KEYS } from './components/sheet/timesheetGroups'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

const ScheduleSeeder: React.FC<{
  dates: string[]
  children: React.ReactNode
}> = ({ dates, children }) => {
  const { setSelectedDays } = useSchedule()
  React.useEffect(() => {
    setSelectedDays(dates)
  }, [])
  return <>{children}</>
}

interface TimesheetProps {
  companyId: string
  driverId: string
  month: Moment
  onTimesheetDataChange?: (data: {
    calculationDate: string | null
    calculationUser: string | null
    calculationUserEmail: string | null
  }) => void
  onCalculate?: () => void
  selectedCalculateMonths?: string[]
  onMonthsChange?: (months: string[]) => void
  isCalculating: boolean
  setIsCalculating: React.Dispatch<React.SetStateAction<boolean>>
  previewTimesheet?: TimesheetModel | null
  showOldTimesheet?: boolean
  onTimesheetLoaded?: (model: TimesheetModel | null) => void
  onExplainRow?: (rowKey: string) => void
  showChart: boolean
}

const Timesheet: React.FC<TimesheetProps> = ({
  companyId,
  driverId,
  month,
  onTimesheetDataChange,
  onCalculate,
  selectedCalculateMonths,
  onMonthsChange,
  isCalculating,
  setIsCalculating,
  previewTimesheet,
  showOldTimesheet = false,
  onTimesheetLoaded,
  onExplainRow,
  showChart,
}) => {
  const { t } = useLocalize()
  const { getTimesheet } = useTimesheetService()
  const { getPinnedDays } = useDayTypesService()
  const {
    isLoading: externalLoading,
    setSelectedDays: setTimesheetSelectedDays,
  } = useTimesheet()
  const { completedTasks } = useTask()

  const [timesheet, setTimesheet] = React.useState<TimesheetModel | null>(null)
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [isDayTypesModalOpen, setIsDayTypesModalOpen] = React.useState(false)
  const [showInfoPopup, setShowInfoPopup] = React.useState(false)
  const [pinnedDays, setPinnedDays] = React.useState<DayType[]>([])
  const [isPinnedLoading, setIsPinnedLoading] = React.useState(true)
  const [contextMenuOpen, setContextMenuOpen] = React.useState(false)
  const [contextMenuPosition, setContextMenuPosition] = React.useState({
    x: 0,
    y: 0,
  })
  const [selectedColumns, setSelectedColumns] = React.useState<number[]>([])
  const [containerWidth, setContainerWidth] = React.useState(0)
  const scheduleRefreshKey = useScheduleStore((s) => s.refreshKey)
  const refreshSchedule = useScheduleStore((s) => s.refresh)

  const [selectedRows, setSelectedRows] = React.useState<number[]>([])

  const [hoveredColumn, setHoveredColumn] = React.useState<number | null>(null)
  const [hoveredRow, setHoveredRow] = React.useState<number | null>(null)

  const lastClickedColumnRef = React.useRef<number | null>(null)
  const lastClickedRowRef = React.useRef<number | null>(null)
  const isDraggingRef = React.useRef(false)
  const dragStartRef = React.useRef<number | null>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const height = 22
  const HEADER_HEIGHT = 46
  const MIN_CELL_WIDTH = 40
  const SUMMARY_COLUMN_WIDTH = 68
  const SUMMARY_TOTAL_WIDTH = SUMMARY_COLUMN_WIDTH * 2 + 12
  const displayTimesheet =
    previewTimesheet && !showOldTimesheet ? previewTimesheet : timesheet
  const dayCount = displayTimesheet?.dayActivities?.length ?? 31
  const rows = (displayTimesheet && displayTimesheet.rows) || []
  const calculationExists =
    (displayTimesheet && displayTimesheet.calculationExists) ||
    !!previewTimesheet
  const rowInfo =
    (displayTimesheet && displayTimesheet.rows.map((r) => r)) || []

  const computedCellWidth = React.useMemo(() => {
    if (containerWidth <= 0) return MIN_CELL_WIDTH
    const availableForDays =
      Math.min(containerWidth, 2000) - SUMMARY_TOTAL_WIDTH
    const perDay = Math.floor(availableForDays / dayCount)
    return Math.max(MIN_CELL_WIDTH, perDay - 1)
  }, [containerWidth, dayCount])

  const widthValue = computedCellWidth

  const selectedDates = React.useMemo(() => {
    if (!displayTimesheet?.dayActivities || selectedColumns.length === 0)
      return []
    const dates: string[] = []
    for (const i of selectedColumns) {
      const day = displayTimesheet.dayActivities[i]
      if (day)
        dates.push(moment(day.activityDayLocalTimeDate).format('YYYY-MM-DD'))
    }
    return dates
  }, [selectedColumns, displayTimesheet?.dayActivities])

  const workingDayChartData = React.useMemo(() => {
    const empty = {
      selectedDate: null as string | null,
      plannedWorkTime: null as string | null,
      actualWorkPlan: null as string | null,
      dayStart: null as string | null,
      dayEnd: null as string | null,
    }
    if (!displayTimesheet || selectedColumns.length !== 1) return empty
    const dayIndex = selectedColumns[0]
    const day = displayTimesheet.dayActivities?.[dayIndex]
    if (!day) return empty
    const findCell = (key: string) => {
      const row = displayTimesheet.rows.find((r) => r.titleKey === key)
      const v = row?.cells?.[dayIndex]?.value
      return v && v !== '-' ? v : null
    }
    return {
      selectedDate: moment(day.activityDayLocalTimeDate).format('YYYY-MM-DD'),
      plannedWorkTime: findCell('PlannedScheduledWorkTimeTimesheetEvent'),
      actualWorkPlan: findCell('ActualScheduledWorkTimeTimesheetEvent'),
      dayStart: findCell('StartTimeTimesheetEvent'),
      dayEnd: findCell('EndTimeTimesheetEvent'),
    }
  }, [displayTimesheet, selectedColumns])

  const setRange = (a: number, b: number) => {
    const min = Math.min(a, b)
    const max = Math.max(a, b)
    const cols: number[] = []
    for (let i = min; i <= max; i++) cols.push(i)
    setSelectedColumns(cols)
  }

  const selectedColumnsSet = React.useMemo(
    () => new Set(selectedColumns),
    [selectedColumns],
  )

  const isInSelection = React.useCallback(
    (idx: number) => selectedColumnsSet.has(idx),
    [selectedColumnsSet],
  )

  const isFirstSelected = React.useCallback(
    (idx: number) =>
      selectedColumnsSet.has(idx) && !selectedColumnsSet.has(idx - 1),
    [selectedColumnsSet],
  )

  const isLastSelected = React.useCallback(
    (idx: number) =>
      selectedColumnsSet.has(idx) && !selectedColumnsSet.has(idx + 1),
    [selectedColumnsSet],
  )

  const selectedRowsSet = React.useMemo(
    () => new Set(selectedRows),
    [selectedRows],
  )

  const isRowInSelection = React.useCallback(
    (idx: number) => selectedRowsSet.has(idx),
    [selectedRowsSet],
  )

  const isFirstRowSelected = React.useCallback(
    (idx: number) => selectedRowsSet.has(idx) && !selectedRowsSet.has(idx - 1),
    [selectedRowsSet],
  )

  const isLastRowSelected = React.useCallback(
    (idx: number) => selectedRowsSet.has(idx) && !selectedRowsSet.has(idx + 1),
    [selectedRowsSet],
  )

  const setRowRange = (a: number, b: number) => {
    const min = Math.min(a, b)
    const max = Math.max(a, b)
    const rs: number[] = []
    for (let i = min; i <= max; i++) rs.push(i)
    setSelectedRows(rs)
  }

  const handleRowClick = React.useCallback(
    (rowIndex: number, e: React.MouseEvent) => {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey
      if (isCtrlOrCmd) {
        setSelectedRows((prev) => {
          if (prev.includes(rowIndex)) return prev.filter((r) => r !== rowIndex)
          return [...prev, rowIndex].sort((a, b) => a - b)
        })
        lastClickedRowRef.current = rowIndex
      } else if (e.shiftKey && lastClickedRowRef.current !== null) {
        setRowRange(lastClickedRowRef.current, rowIndex)
      } else {
        setSelectedRows((prev) => {
          if (prev.length === 1 && prev[0] === rowIndex) return []
          return [rowIndex]
        })
        lastClickedRowRef.current = rowIndex
      }
    },
    [],
  )

  const handleColumnClick = React.useCallback(
    (columnIndex: number, e: React.MouseEvent) => {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey
      if (isCtrlOrCmd) {
        setSelectedColumns((prev) => {
          if (prev.includes(columnIndex)) {
            return prev.filter((c) => c !== columnIndex)
          } else {
            return [...prev, columnIndex].sort((a, b) => a - b)
          }
        })
        lastClickedColumnRef.current = columnIndex
      } else if (e.shiftKey && lastClickedColumnRef.current !== null) {
        setRange(lastClickedColumnRef.current, columnIndex)
      } else {
        setSelectedColumns((prev) => {
          if (prev.length === 1 && prev[0] === columnIndex) {
            return []
          }
          return [columnIndex]
        })
        lastClickedColumnRef.current = columnIndex
      }
    },
    [],
  )
  const fetchPinnedDays = React.useCallback(async () => {
    setIsPinnedLoading(true)
    try {
      const data = await getPinnedDays({ companyId })
      setPinnedDays(data)
    } catch (error) {
      console.error('Error fetching pinned days:', error)
    } finally {
      setIsPinnedLoading(false)
    }
  }, [companyId])

  const handleColumnMouseDown = React.useCallback(
    (columnIndex: number, e: React.MouseEvent) => {
      if (e.button !== 0) return
      if (e.ctrlKey || e.metaKey || e.shiftKey) return
      isDraggingRef.current = true
      dragStartRef.current = columnIndex
    },
    [],
  )

  const handleColumnMouseEnter = React.useCallback((columnIndex: number) => {
    setHoveredColumn(columnIndex)
    if (!isDraggingRef.current || dragStartRef.current === null) return
    setRange(dragStartRef.current, columnIndex)
    lastClickedColumnRef.current = dragStartRef.current
  }, [])

  const handleColumnContextMenu = React.useCallback(
    (columnIndex: number, e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      flushSync(() => {
        setSelectedColumns((prev) => {
          if (prev.includes(columnIndex)) return prev
          lastClickedColumnRef.current = columnIndex
          return [columnIndex]
        })
      })
      setContextMenuPosition({ x: e.clientX, y: e.clientY })
      setContextMenuOpen(true)
    },
    [],
  )

  React.useEffect(() => {
    const handleMouseUp = () => {
      isDraggingRef.current = false
    }
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [])

  const determineStartDate = React.useCallback((date: Moment): Moment => {
    return moment(new Date(date.year(), date.month(), 1))
  }, [])

  React.useEffect(() => {
    let cancelled = false

    if (!driverId) {
      return () => {
        cancelled = true
      }
    }

    const fetchTimesheet = async () => {
      setIsLoading(true)
      try {
        const startDate = determineStartDate(month)
        const result = await getTimesheet({
          companyId,
          driverId,
          date: startDate,
        })
        if (cancelled) return

        setTimesheet(result)
        if (onTimesheetLoaded) onTimesheetLoaded(result)

        if (onTimesheetDataChange && result) {
          const calculationDate = result.calculationDate
            ? moment(result.calculationDate).format(TIME_FORMAT)
            : null
          const calculationUser = result.calculationUser || null
          const calculationUserEmail = result.calculationUserEmail || null
          onTimesheetDataChange({
            calculationDate,
            calculationUser,
            calculationUserEmail,
          })
        } else if (onTimesheetDataChange) {
          onTimesheetDataChange({
            calculationDate: null,
            calculationUser: null,
            calculationUserEmail: null,
          })
        }
      } catch (error) {
        if (cancelled) return
        console.error('Error fetching timesheet:', error)
        setTimesheet(null)
        if (onTimesheetDataChange) {
          onTimesheetDataChange({
            calculationDate: null,
            calculationUser: null,
            calculationUserEmail: null,
          })
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchTimesheet()
    return () => {
      cancelled = true
    }
  }, [companyId, driverId, scheduleRefreshKey, month])

  React.useEffect(() => {
    const currentContainer = containerRef.current
    if (!currentContainer) return () => {}
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width)
      }
    })
    observer.observe(currentContainer)
    return () => observer.disconnect()
  }, [])

  React.useEffect(() => {
    setTimesheetSelectedDays(selectedDates)
  }, [selectedDates])

  React.useEffect(() => {
    fetchPinnedDays()
  }, [])

  React.useEffect(() => {
    if (!isCalculating) return
    const done = completedTasks.some(
      (task) =>
        task.type === TaskType.Timesheet &&
        (task.status === TaskStatus.Completed ||
          task.status === TaskStatus.Error),
    )
    if (done) {
      setIsCalculating(false)
      refreshSchedule()
    }
  }, [completedTasks, isCalculating])

  return (
    <div
      className="tw-flex tw-flex-1"
      style={{ maxWidth: 2000, maxHeight: 1000, overflow: 'auto' }}
      onMouseLeave={() => {
        setHoveredColumn(null)
        setHoveredRow(null)
      }}
    >
      {calculationExists && !isCalculating && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
          }}
        >
          {showChart && (
            <WorkingDayChart
              {...workingDayChartData}
              companyId={companyId}
              driverId={Number(driverId)}
            />
          )}
          <div
            style={{
              position: 'relative',
              marginTop: showChart ? 10 : 0,
              padding: 0,
            }}
            className="tw-mx-auto tw-w-full tw-px-[15px] main-content--wrapper timesheet-wrapper"
          >
            <div style={{ width: '100%' }}>
              <div
                className="year-inner"
                style={{
                  padding: 4,
                  paddingTop: 0,
                  width: '100%',
                }}
              >
                {!isLoading && (
                  <FlexView
                    hAlignContent="left"
                    vAlignContent="top"
                    style={{
                      border: 'solid',
                      borderWidth: 0,
                      // marginTop: 5
                    }}
                  >
                    <FlexView
                      hAlignContent="center"
                      vAlignContent="center"
                      width={160}
                    >
                      <FlexView column style={{ marginLeft: -10 }}>
                        <div
                          style={{ height: HEADER_HEIGHT, width: 160 }}
                          className="tw-bg-base-100"
                        />

                        <RowInfoItem
                          width={160}
                          height={height}
                          headerHeight={0}
                          rowInfo={rowInfo}
                          selectedRows={selectedRows}
                          hoveredRow={hoveredRow}
                          onRowClick={handleRowClick}
                          onRowMouseEnter={(idx: number) => {
                            setHoveredRow(idx)
                            setHoveredColumn(null)
                          }}
                          onRowMouseLeave={() => setHoveredRow(null)}
                          onExplainRow={onExplainRow}
                        />
                      </FlexView>
                    </FlexView>

                    <FlexView
                      column
                      hAlignContent="center"
                      vAlignContent="center"
                      style={{
                        border: 'solid',
                        borderWidth: 0,
                        marginLeft: -5,
                        flex: 1,
                        overflow: 'hidden',
                        maxWidth: 2000,
                      }}
                    >
                      <div
                        ref={containerRef}
                        style={{
                          marginLeft: 0,
                          overflowX: 'auto',
                          width: '100%',
                        }}
                      >
                        <FlexView
                          column
                          hAlignContent="left"
                          vAlignContent="center"
                          style={{
                            border: 'solid',
                            borderWidth: 0,
                            whiteSpace: 'nowrap',
                            minWidth: 'min-content',
                            width: '100%',
                          }}
                        >
                          <FlexView
                            hAlignContent="left"
                            vAlignContent="center"
                            style={{ width: '100%' }}
                          >
                            {displayTimesheet?.dayActivities &&
                              displayTimesheet.dayActivities.map(
                                (day: TimesheetDayActivityModel, dayIndex) => (
                                  <HeaderDayItem
                                    key={dayIndex}
                                    day={day}
                                    height={HEADER_HEIGHT}
                                    width={widthValue + 1}
                                    isSelected={isInSelection(dayIndex)}
                                    isFirstSelected={isFirstSelected(dayIndex)}
                                    isLastSelected={isLastSelected(dayIndex)}
                                    isHovered={hoveredColumn === dayIndex}
                                    onClick={(e: React.MouseEvent) =>
                                      handleColumnClick(dayIndex, e)
                                    }
                                    onMouseDown={(e: React.MouseEvent) =>
                                      handleColumnMouseDown(dayIndex, e)
                                    }
                                    onMouseEnter={() => {
                                      handleColumnMouseEnter(dayIndex)
                                      setHoveredRow(null)
                                    }}
                                    onContextMenu={(e: React.MouseEvent) =>
                                      handleColumnContextMenu(dayIndex, e)
                                    }
                                  />
                                ),
                              )}
                            <SummaryHeaderItem
                              height={HEADER_HEIGHT}
                              width={widthValue + 1 + 24}
                              isHighlighted={false}
                            />
                          </FlexView>

                          {rows &&
                            rows.map((item: TimesheetRowModel, itemIndex) => {
                              const rowHeight = item.isWorkTimeEvent
                                ? height + 6
                                : height + 2
                              const prevTitleKey =
                                itemIndex > 0
                                  ? rows[itemIndex - 1]?.titleKey
                                  : undefined
                              const isAfterBoundary =
                                !!prevTitleKey &&
                                GROUP_END_TITLE_KEYS.has(prevTitleKey)
                              const isBoundary =
                                !!item.titleKey &&
                                GROUP_END_TITLE_KEYS.has(item.titleKey)
                              return (
                                <React.Fragment key={itemIndex}>
                                  <FlexView
                                    hAlignContent="center"
                                    vAlignContent="center"
                                    style={{
                                      height: rowHeight,
                                      marginTop:
                                        itemIndex > 0 && !isAfterBoundary
                                          ? -1
                                          : 0,
                                      width: '100%',
                                    }}
                                  >
                                    {item.cells.map(
                                      (cell: TimesheetCellModel, cellIndex) => {
                                        const isFirst = itemIndex === 0
                                        const prevDayType =
                                          cellIndex > 0
                                            ? item.cells[cellIndex - 1]?.dayType
                                            : undefined
                                        const nextDayType =
                                          cellIndex < item.cells.length - 1
                                            ? item.cells[cellIndex + 1]?.dayType
                                            : undefined
                                        const isFirstInGroup =
                                          isFirst &&
                                          cell.dayType !== prevDayType
                                        const isLastInGroup =
                                          isFirst &&
                                          cell.dayType !== nextDayType
                                        const savedCell = previewTimesheet
                                          ? timesheet?.rows[itemIndex]?.cells[
                                              cellIndex
                                            ]
                                          : null
                                        const isModified =
                                          !!previewTimesheet &&
                                          !showOldTimesheet &&
                                          !!timesheet?.calculationExists &&
                                          savedCell?.value !== cell.value
                                        const isSummaryCell =
                                          cellIndex >= item.cells.length - 2
                                        return (
                                          <CellItem
                                            key={cellIndex}
                                            index={cellIndex}
                                            itemsCount={item.cells.length}
                                            work={item.isWorkTimeEvent}
                                            cell={cell}
                                            dayTypeName={
                                              isFirst && cell.dayType
                                                ? t(`dayType.${CellDayType[cell.dayType]}`)
                                                : undefined
                                            }
                                            isModified={isModified}
                                            width={widthValue + 1}
                                            isFirstRow={itemIndex === 0}
                                            isLastRow={
                                              itemIndex === rows.length - 1
                                            }
                                            isFirstInGroup={isFirstInGroup}
                                            isLastInGroup={isLastInGroup}
                                            isSelected={isInSelection(
                                              cellIndex,
                                            )}
                                            isFirstSelected={isFirstSelected(
                                              cellIndex,
                                            )}
                                            isLastSelected={isLastSelected(
                                              cellIndex,
                                            )}
                                            isRowSelected={isRowInSelection(
                                              itemIndex,
                                            )}
                                            isFirstRowSelected={isFirstRowSelected(
                                              itemIndex,
                                            )}
                                            isLastRowSelected={isLastRowSelected(
                                              itemIndex,
                                            )}
                                            isSummaryHighlighted={
                                              isRowInSelection(itemIndex) &&
                                              isSummaryCell
                                            }
                                            isColumnHovered={
                                              hoveredColumn === cellIndex &&
                                              !isSummaryCell
                                            }
                                            isRowHovered={
                                              hoveredRow === itemIndex &&
                                              !isSummaryCell
                                            }
                                            onClick={(e: React.MouseEvent) =>
                                              handleColumnClick(cellIndex, e)
                                            }
                                            onMouseDown={(
                                              e: React.MouseEvent,
                                            ) =>
                                              handleColumnMouseDown(
                                                cellIndex,
                                                e,
                                              )
                                            }
                                            onMouseEnter={() => {
                                              handleColumnMouseEnter(cellIndex)
                                              setHoveredRow(itemIndex)
                                            }}
                                            onContextMenu={(
                                              e: React.MouseEvent,
                                            ) =>
                                              handleColumnContextMenu(
                                                cellIndex,
                                                e,
                                              )
                                            }
                                          />
                                        )
                                      },
                                    )}
                                  </FlexView>
                                  {isBoundary && (
                                    <div
                                      style={{
                                        height: 1,
                                        backgroundColor: '#0034FF',
                                        opacity: 0.5,
                                        width: '100%',
                                      }}
                                    />
                                  )}
                                </React.Fragment>
                              )
                            })}
                        </FlexView>
                      </div>
                    </FlexView>
                  </FlexView>
                )}
              </div>
            </div>
          </div>
          <div className="tw-sticky tw-bottom-0 tw-z-[90] tw-mt-auto tw-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-1 tw-border-t tw-border-base-300 tw-bg-base-100 tw-px-4 tw-py-2 tw-text-xs">
            <span className="tw-text-content-base">
              <span className="tw-text-content-muted">
                {t('timesheet.lastCalculationDate')}:
              </span>{' '}
              <span className="tw-font-medium">
                {timesheet?.calculationDate
                  ? moment(timesheet.calculationDate).format('DD.MM.YYYY HH:mm')
                  : '—'}
              </span>
            </span>
            <span className="tw-text-content-base">
              <span className="tw-text-content-muted">
                {t('timesheet.lastCalculationUser')}:
              </span>{' '}
              <span className="tw-font-medium">
                {timesheet?.calculationUser ||
                  timesheet?.calculationUserEmail ||
                  '—'}
              </span>
            </span>
            <span className="tw-text-content-base">
              <span className="tw-text-content-muted">
                {t('timesheet.lastCalculationVersion')}:
              </span>{' '}
              <span className="tw-font-medium">
                {timesheet?.applicationVersion ?? '—'}
              </span>
            </span>
          </div>
        </div>
      )}
      {!isCalculating &&
        !calculationExists &&
        (isLoading || externalLoading) && (
          <div className="tw-absolute tw-inset-0 tw-z-[100] tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-bg-white/80 tw-backdrop-blur-sm">
            <LoaderIcon className="tw-w-16 tw-animate-spin" />
          </div>
        )}

      {!isCalculating &&
        !calculationExists &&
        !isLoading &&
        !externalLoading && (
          <EmptyState
            onCalculate={onCalculate}
            selectedCalculateMonths={selectedCalculateMonths}
            onMonthsChange={onMonthsChange}
            companyId={companyId}
            driverId={driverId}
          />
        )}
      <TimeSheetContextMenu
        isOpen={contextMenuOpen}
        driverId={driverId}
        companyId={companyId}
        position={contextMenuPosition}
        onClose={() => setContextMenuOpen(false)}
        selectedDates={selectedDates}
        onChangeDayType={() => setIsDayTypesModalOpen(true)}
        onSettingsChanged={() => setShowInfoPopup(true)}
      />
      {isDayTypesModalOpen && (
        <ScheduleProvider>
          <ScheduleSeeder dates={selectedDates}>
            <TimesheetDayTypePanel
              companyId={companyId}
              driverId={driverId}
              pinnedDays={pinnedDays}
              isPinnedLoading={isPinnedLoading}
              fetchPinnedDays={fetchPinnedDays}
              onClose={() => setIsDayTypesModalOpen(false)}
              onDayTypeChanged={() => setShowInfoPopup(true)}
            />
          </ScheduleSeeder>
        </ScheduleProvider>
      )}
      {showInfoPopup && (
        <TimesheetInfoPopup
          onCalculate={() => {
            setIsCalculating(true)
            onCalculate?.()
          }}
          onClose={() => {
            ;(setShowInfoPopup(false), setTimesheetSelectedDays([]))
          }}
        />
      )}
      {isCalculating && (
        <div className="tw-absolute tw-inset-0 tw-z-[100] tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-bg-white/80 tw-backdrop-blur-sm">
          <LoaderIcon className="tw-w-16 tw-animate-spin" />
          <span className="tw-text-base tw-font-medium tw-text-content-base">
            Trwa przeliczanie ewidencji...
          </span>
          <span className="tw-text-sm tw-text-content-base/70">
            Nie zamykaj okna do zakończenia operacji
          </span>
        </div>
      )}
    </div>
  )
}

export default Timesheet
